'use client';

import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import type { LocationPreferences } from '@/features/locations/domain/model/location-preferences';
import { useLocationPreference } from '@/features/totems/presentation/hooks/web-totem/use-location-preference';
import { useOptionalTotem } from '@/features/totems/presentation/providers/web-totem/totem-provider';
import { noop } from '@/shared/utils/noop';
import { cn } from '@/shared/utils/utils';

export interface TotemOrderOptionsOverlayProps {
  orderOptionsOpen?: boolean;
  setOrderOptionsOpen?: (open: boolean) => void;
  orderType?: 'takeaway' | 'eat_here' | null;
  setOrderType?: (type: 'takeaway' | 'eat_here' | null) => void;
  pickupMethod?: 'banderin' | 'beeper' | 'nombre' | null;
  setPickupMethod?: (method: 'banderin' | 'beeper' | 'nombre' | null) => void;
  beeperNumber?: string;
  setBeeperNumber?: (num: string) => void;
  locationTitle?: string;
  locationPreference?: LocationPreferences | null;
}

export function TotemOrderOptionsOverlay(props: TotemOrderOptionsOverlayProps = {}) {
  const totemCtx = useOptionalTotem();
  const { data: queryLocationPreference } = useLocationPreference(
    props.locationPreference ? undefined : (totemCtx?.totem?.location_id ?? undefined)
  );

  const orderOptionsOpen = props.orderOptionsOpen ?? totemCtx?.orderOptionsOpen ?? false;
  const setOrderOptionsOpen = props.setOrderOptionsOpen ?? totemCtx?.setOrderOptionsOpen ?? noop;
  const orderType = props.orderType !== undefined ? props.orderType : (totemCtx?.orderType ?? null);
  const setOrderType = props.setOrderType ?? totemCtx?.setOrderType ?? noop;
  const pickupMethod = props.pickupMethod !== undefined ? props.pickupMethod : (totemCtx?.pickupMethod ?? null);
  const setPickupMethod = props.setPickupMethod ?? totemCtx?.setPickupMethod ?? noop;
  const beeperNumber = props.beeperNumber !== undefined ? props.beeperNumber : (totemCtx?.beeperNumber ?? '');
  const setBeeperNumber = props.setBeeperNumber ?? totemCtx?.setBeeperNumber ?? noop;
  const locationTitle = props.locationTitle ?? totemCtx?.totem?.locations?.title ?? '';
  const locationPreference = props.locationPreference ?? queryLocationPreference;

  const pathname = usePathname();
  const isDashboard = pathname?.includes('/takeorder');

  const [step, setStep] = useState<1 | 2>(1);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasTakeaway = Boolean(locationPreference?.take_away);
  const hasEatHere = Boolean(locationPreference?.eat_here);
  const hasFlag = Boolean(locationPreference?.flag);
  const hasBeeper = Boolean(locationPreference?.beeper);
  const hasName = Boolean(locationPreference?.name);
  const eatHereMethodCount = (hasFlag ? 1 : 0) + (hasBeeper ? 1 : 0) + (hasName ? 1 : 0);

  const singleMethod = useMemo<'banderin' | 'beeper' | 'nombre' | null>(() => {
    if (eatHereMethodCount !== 1) return null;
    if (hasFlag) return 'banderin';
    if (hasBeeper) return 'beeper';
    if (hasName) return 'nombre';
    return null;
  }, [eatHereMethodCount, hasFlag, hasBeeper, hasName]);

  useEffect(() => {
    if (pickupMethod !== null) {
      const timeout = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [pickupMethod]);

  // Dynamic step initialization based on hybrid flow
  useEffect(() => {
    if (orderOptionsOpen) {
      if (orderType === 'eat_here' || (!hasTakeaway && hasEatHere)) {
        setStep(2);
        if (singleMethod && !pickupMethod) {
          setPickupMethod(singleMethod);
        }
      } else {
        setStep(1);
      }
    }
  }, [orderOptionsOpen, orderType, hasTakeaway, hasEatHere, singleMethod, pickupMethod, setPickupMethod]);

  if (!orderOptionsOpen) return null;

  const canDismiss =
    orderType !== null &&
    (orderType === 'takeaway' || eatHereMethodCount === 0 || (pickupMethod !== null && beeperNumber.trim() !== ''));

  const handleTakeaway = () => {
    setOrderType('takeaway');
    setPickupMethod(null);
    setBeeperNumber('');
    setOrderOptionsOpen(false);
  };

  const handleDineIn = () => {
    setOrderType('eat_here');
    if (eatHereMethodCount === 0) {
      setPickupMethod(null);
      setBeeperNumber('');
      setOrderOptionsOpen(false);
      return;
    }
    if (singleMethod) {
      setPickupMethod(singleMethod);
    }
    setStep(2);
  };

  const handleContinue = () => {
    setOrderOptionsOpen(false);
  };

  const handleBack = () => {
    if (pickupMethod !== null && eatHereMethodCount > 1) {
      setPickupMethod(null);
      setBeeperNumber('');
    } else if (step === 2 && hasTakeaway && hasEatHere) {
      setStep(1);
    } else if (canDismiss) {
      setOrderOptionsOpen(false);
    }
  };

  let wrapperScaleClass = '';
  if (pickupMethod !== null) {
    wrapperScaleClass = 'scale-[0.70] origin-top transition-transform duration-300';
  } else {
    wrapperScaleClass = 'scale-[0.75] origin-top transition-transform duration-300';
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={cn(
        'inset-0 z-[100] flex flex-col overflow-x-hidden overflow-y-auto overscroll-contain bg-black/75 px-6 pt-10 backdrop-blur-sm',
        isDashboard ? 'absolute' : 'fixed'
      )}
    >
      <div className={cn('flex w-full flex-1 flex-col transition-transform', wrapperScaleClass)}>
        {/* Back Button */}
        {step === 2 && (hasTakeaway || (eatHereMethodCount > 1 && pickupMethod !== null)) && (
          <Button
            variant="ghost"
            className="absolute top-10 left-6 rounded-full py-6 pr-5 pl-3 text-lg font-semibold text-white hover:bg-white/10 hover:text-white"
            onClick={handleBack}
          >
            <div className="mr-3 rounded-full bg-white p-1.5 text-black">
              <ArrowLeft className="size-5" />
            </div>
            Volver
          </Button>
        )}

        {/* Close Button (if re-opened with already selected options) */}
        {canDismiss && (
          <button
            type="button"
            onClick={() => setOrderOptionsOpen(false)}
            aria-label="Cerrar"
            className="absolute top-10 right-6 rounded-full p-3 text-white transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="size-8" />
          </button>
        )}

        {/* Header Logo/Title */}
        <div className="mt-0 mb-4 flex flex-col items-center gap-2 text-white xl:mt-4 xl:mb-10">
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-bold">{locationTitle}</h2>
          </div>
          {step === 2 && (
            <p className="mt-4 mb-2 text-xl xl:mt-8 xl:mb-4 xl:text-2xl">
              Seleccioná una opción para retirar tu pedido
            </p>
          )}
        </div>

        <div className="flex flex-1 flex-col items-center justify-center pb-10 xl:pb-20">
          {step === 1 ? (
            <div className="flex w-full max-w-lg flex-col gap-4 px-4 sm:gap-6 sm:px-0">
              {hasTakeaway && (
                <button
                  type="button"
                  onClick={handleTakeaway}
                  className="group flex items-center justify-center gap-4 rounded-3xl bg-white p-6 text-black shadow-2xl transition-transform hover:bg-slate-50 active:scale-[0.98] sm:gap-6 sm:p-8 lg:gap-8 lg:p-10"
                >
                  <div className="relative h-12 w-12 shrink-0 sm:h-16 sm:w-16 lg:h-20 lg:w-20">
                    <Image src="/icons/take-away.png" alt="Para llevar" fill className="object-contain" />
                  </div>
                  <span className="text-xl font-bold whitespace-nowrap sm:text-2xl lg:text-3xl">Para llevar</span>
                </button>
              )}

              {hasEatHere && (
                <button
                  type="button"
                  onClick={handleDineIn}
                  className="group flex items-center justify-center gap-4 rounded-3xl bg-white p-6 text-black shadow-2xl transition-transform hover:bg-slate-50 active:scale-[0.98] sm:gap-6 sm:p-8 lg:gap-8 lg:p-10"
                >
                  <div className="relative h-12 w-12 shrink-0 sm:h-16 sm:w-16 lg:h-20 lg:w-20">
                    <Image
                      src="/icons/eat-here.png"
                      alt="Para comer aquí"
                      fill
                      className="object-contain brightness-0"
                    />
                  </div>
                  <span className="text-xl font-bold whitespace-nowrap sm:text-2xl lg:text-3xl">Para comer aquí</span>
                </button>
              )}
            </div>
          ) : (
            <div className="flex w-full max-w-3xl flex-col items-center gap-6 xl:gap-12">
              {eatHereMethodCount > 1 && (
                <div className="flex w-full justify-center gap-4 xl:gap-6">
                  {hasFlag && (
                    <button
                      type="button"
                      onClick={() => {
                        if (pickupMethod !== 'banderin') setBeeperNumber('');
                        setPickupMethod('banderin');
                      }}
                      className={cn(
                        'flex flex-1 flex-col items-center gap-3 rounded-3xl border-4 bg-white p-4 shadow-xl transition-all active:scale-[0.98] xl:gap-6 xl:p-10',
                        pickupMethod === 'banderin' ? 'scale-105 border-emerald-500' : 'border-transparent opacity-90'
                      )}
                    >
                      <Image
                        src="/icons/banderin-modal.png"
                        alt="Banderín"
                        width={96}
                        height={96}
                        className="h-16 w-16 object-contain xl:h-24 xl:w-24"
                      />
                      <span className="text-xl font-semibold text-zinc-800 xl:text-2xl">Banderín</span>
                    </button>
                  )}

                  {hasBeeper && (
                    <button
                      type="button"
                      onClick={() => {
                        if (pickupMethod !== 'beeper') setBeeperNumber('');
                        setPickupMethod('beeper');
                      }}
                      className={cn(
                        'flex flex-1 flex-col items-center gap-3 rounded-3xl border-4 bg-white p-4 shadow-xl transition-all active:scale-[0.98] xl:gap-6 xl:p-10',
                        pickupMethod === 'beeper' ? 'scale-105 border-emerald-500' : 'border-transparent opacity-90'
                      )}
                    >
                      <Image
                        src="/icons/beeper.png"
                        alt="Beeper"
                        width={96}
                        height={96}
                        className="h-16 w-16 object-contain xl:h-24 xl:w-24"
                      />
                      <span className="text-xl font-semibold text-zinc-800 xl:text-2xl">Beeper</span>
                    </button>
                  )}

                  {hasName && (
                    <button
                      type="button"
                      onClick={() => {
                        if (pickupMethod !== 'nombre') setBeeperNumber('');
                        setPickupMethod('nombre');
                      }}
                      className={cn(
                        'flex flex-1 flex-col items-center gap-3 rounded-3xl border-4 bg-white p-4 shadow-xl transition-all active:scale-[0.98] xl:gap-6 xl:p-10',
                        pickupMethod === 'nombre' ? 'scale-105 border-emerald-500' : 'border-transparent opacity-90'
                      )}
                    >
                      <Image
                        src="/icons/nombre.png"
                        alt="Nombre"
                        width={96}
                        height={96}
                        className="h-16 w-16 object-contain xl:h-24 xl:w-24"
                      />
                      <span className="text-xl font-semibold text-zinc-800 xl:text-2xl">Nombre</span>
                    </button>
                  )}
                </div>
              )}

              {/* Identifier Input and Continue Button */}
              {pickupMethod !== null && (
                <div className="w-full max-w-2xl rounded-3xl bg-white p-3 shadow-2xl duration-300 animate-in fade-in slide-in-from-top-4 sm:p-4 xl:p-8">
                  <label
                    htmlFor="identifier-input"
                    className="mb-2 block text-base font-semibold text-zinc-800 sm:text-lg xl:mb-4 xl:text-2xl"
                  >
                    {pickupMethod === 'banderin' && 'Ingresá el número de banderín'}
                    {pickupMethod === 'beeper' && 'Ingresá el número de beeper'}
                    {pickupMethod === 'nombre' && 'Ingresá tu nombre'}
                  </label>
                  <div className="flex items-center gap-2 sm:gap-4">
                    <div className="relative flex-1">
                      <input
                        ref={inputRef}
                        id="identifier-input"
                        type={pickupMethod === 'nombre' ? 'text' : 'number'}
                        value={beeperNumber}
                        maxLength={pickupMethod === 'nombre' ? 20 : 3}
                        onChange={e => {
                          const val = e.target.value;
                          if (pickupMethod !== 'nombre') {
                            if (val.length <= 3) setBeeperNumber(val);
                          } else {
                            if (val.length <= 20) setBeeperNumber(val);
                          }
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && beeperNumber.trim()) {
                            handleContinue();
                          }
                        }}
                        placeholder={
                          pickupMethod === 'beeper'
                            ? 'Ingresa el número'
                            : pickupMethod === 'banderin'
                              ? 'Ingresa el número'
                              : 'Tu nombre'
                        }
                        className="h-12 w-full [appearance:textfield] rounded-2xl bg-slate-100 px-10 text-center text-lg font-bold text-zinc-800 transition-all outline-none placeholder:text-sm placeholder:font-medium placeholder:text-slate-400 focus:ring-4 focus:ring-emerald-500/20 sm:h-14 sm:px-16 sm:text-2xl sm:placeholder:text-base xl:h-24 xl:px-24 xl:text-4xl xl:placeholder:text-2xl dark:bg-slate-100 dark:text-zinc-800 dark:placeholder:text-slate-400 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <span className="absolute top-1/2 right-3 -translate-y-1/2 text-xs font-medium text-zinc-400 sm:right-4 sm:text-sm xl:right-6 xl:text-lg">
                        {beeperNumber.length}/{pickupMethod === 'nombre' ? 20 : 3}
                      </span>
                    </div>

                    <Button
                      size="icon"
                      onClick={handleContinue}
                      disabled={beeperNumber.trim().length === 0}
                      className="h-12 w-12 shrink-0 rounded-full bg-emerald-500 text-white shadow-lg transition-all hover:bg-emerald-600 active:scale-[0.98] disabled:opacity-50 sm:h-14 sm:w-14 xl:h-24 xl:w-24"
                    >
                      <ArrowRight className="size-5 stroke-[3] sm:size-6 xl:size-10" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Order Fast Logo */}
        <div className="pointer-events-none mt-auto flex shrink-0 justify-center pb-8 opacity-80">
          <Image
            src="/icons/of-logo.png"
            alt="Order Fast"
            width={160}
            height={60}
            className="object-contain"
            style={{ width: 'auto', height: 'auto' }}
          />
        </div>
      </div>
    </div>
  );
}
